import SessionStore from 'stores/SessionStore';
import { isProd } from 'helpers/helper_functions';
import { last, isEmpty } from 'underscore';
// Available stop types
const STOP_TYPES = [
    'loaded_pickup',
    'loaded_delivery',
    'offsite_loaded_pickup',
    'offsite_loaded_delivery',
    'empty_pickup',
    'empty_delivery',
    'empty_offsite_pickup',
    'empty_offsite_delivery',
];
const MAX_STOP_DEPTH = 3;
/**
 * Generates a flattened array of prefixes for all stop types up to the maximum stop depth.
 * Each prefix is used to refer to specific fields related to a stop type in a shipment.
 *
 * @returns {Array<string>} An array of strings where each string is a prefix corresponding to a stop type and depth.
 * Example Output: ["loaded_pickup__0__", "loaded_pickup__1__", ..., "empty_offsite_delivery__2__"]
 */
export function getStopGroupPrefixes() {
    return Array.from({ length: MAX_STOP_DEPTH }, (_, i) => STOP_TYPES.map((stopType) => `${stopType}__${i}__`)).flat();
}
/**
 * Constructs an array of field names for equipment IDs based on stop group prefixes.
 * This function utilizes getStopGroupPrefixes to generate the base prefixes and appends
 * 'stop_equipment_oid' to each to form the full field names used to identify equipment in a shipment.
 *
 * @returns {Array<string>} An array of field names corresponding to equipment IDs for each stop type and depth.
 * Example Output: ["loaded_pickup__0__stop_equipment_oid", "loaded_pickup__1__stop_equipment_oid", ...]
 */
export function getStopGroupEquipmentFields() {
    return getStopGroupPrefixes().map((prefix) => `${prefix}stop_equipment_oid`);
}
/**
 * Determines if a given shipment object has any equipment associated with it.
 * It checks if any of the equipment fields generated by getStopGroupEquipmentFields are non-empty in the shipment.
 *
 * @param {Object} shipment - The shipment object to check, typically structured as key-value pairs where
 *                            the keys are field names.
 * @returns {boolean} True if any equipment field is non-empty, false otherwise.
 */
export function shipmentHasEquipment(shipment = {}) {
    return getStopGroupEquipmentFields().some((field) => !isEmpty(shipment[field]));
}
/**
 * Transforms stop group data in a shipment object into an array of stops, each represented as an object.
 * This function is necessary due to schema changes where stop groups are flattened and need to be aggregated
 * into arrays to match interfaces expected by bulk actions. It selectively includes stops that have an 'oid'
 * and might aggregate additional details like equipment associated with the stop.
 *
 * @param {Object} shipment - The shipment object containing flattened stop group fields.
 * @returns {Array<Object>} An array of objects where each object represents a stop and includes any associated equipment.
 * Example Structure: [{ stop_oid: "123", equipments: [{ _id: "456", equipment_id: "789" }] }]
 */
export function getStopGroupsArray(shipment = {}) {
    return getStopGroupPrefixes()
        .filter((prefix) => !isEmpty(shipment[`${prefix}stop_oid`]))
        .map((prefix) => {
        const stop = Object.keys(shipment)
            .filter((key) => key.startsWith(prefix))
            .reduce((acc, key) => {
            const attr = last(key.split('__'));
            acc[attr] = shipment[key];
            return acc;
        }, {});
        if ('stop_equipment_id' in stop && !isEmpty(stop.stop_equipment_id)) {
            stop.equipments = [
                {
                    _id: shipment[`${prefix}stop_equipment_oid`],
                    equipment_id: stop.stop_equipment_id,
                },
            ];
        }
        else {
            stop.equipments = [];
        }
        return stop;
    });
}
